import type { FullAliasIBAN, FullCountry, RealIdentity } from "../user/types";
import type { TransactionType } from "../utils/types";
import type { KYCSessionStatus } from "@/features/kyc/types.ts";

export type Kypay = {
  balance: number;
  formattedBalance: string;
  transactions: KypayTransactions[];
  totalPages: number;
  totalItems: number;
  currentPage: number;
};
export enum AmountType {
  GAIN = "GAIN",
  LOSS = "LOSS",
  NEUTRAL = "NEUTRAL"
}
export type KypayTransactions = {
  Type: TransactionType;
  Date: Date;
  NumberInt: number;
  NumberDec: number;
  FlowCredit: string;
  Counterparty: string;
  RefPartner: string;
  ModalTransac: string;
  Description: string;
  EnglishDescription: string;
};

export type KypayTransaction = {
  type: TransactionType;
  typeFr: string;
  date: Date;
  from: string;
  from_en: string;
  to: string;
  to_en: string;
  amount: number;
  amountType: AmountType;
  token_name: string;
  partner_id: number;
  cryptoInformations: {
    fromAmount: number;
    fromAsset: string;
  } | null;
};

export type CryptoAssetes = {
  AssetKey: string;
  Name: string;
  Network: string[];
};
export type CryptoAssetsResponse = { success: boolean; data: CryptoAssetes[] };
export type PolygonTransaction = {
  blockNumber: string;
  timeStamp: string;
  hash: string;
  nonce: string;
  blockHash: string;
  from: string;
  contractAddress: string;
  to: string;
  value: string;
  tokenName: string;
  tokenSymbol: string;
  tokenDecimal: string;
  transactionIndex: string;
  gas: string;
  gasPrice: string;
  gasUsed: string;
  cumulativeGasUsed: string;
  input: string;
  confirmations: string;
};

export type TokenyTransaction = {
  id: string;
  amount: string;
  datetime: string;
  transactionHash: string;
  type: string;
  totalSupplyAfter: string;
  status: string;
  recipient: {
    id: string;
    name: string;
    wallet: string;
    balance: string;
    frozenBalance: string;
    unfrozenBalance: string;
    isWalletFrozen: string;
  };
  sender: {
    id: string;
    name: string;
    wallet: string;
    balance: string;
    frozenBalance: string;
    unfrozenBalance: string;
    isWalletFrozen: string;
  };
  tokenAddress: string;
};

export type GetWalletPolygon2LausanneResponse = {
  success: boolean;
  WalletAddress: string;
};
export type GetWalletPolygonTransactionResponse = {
  success: boolean;
  data: PolygonTransaction[];
};
export type GetWalleTokenyTransactionResponse = {
  success: boolean;
  data: TokenyTransaction[];
};

export type GetUserWalletBalance = {
  balanceWei?: number;
  balanceEther?: number;
};
export type TransactionStatus = {
  session_id: string;
  reference: string;
  status: string;
};

export type GetWalletDataResponse = {
  success: boolean;
  data: WalletData;
};

export type WalletData = {
  status: "InProgress" | "TokenHolder" | "Qualified" | "Failed" | null; // Tokenholder exist but it may never be updated by /wallet_data
  walletAddress: string;
  olkyWallet: boolean | null;
  walletConnect: boolean | null;
};

export type CreateWalletResponse = {
  success: boolean;
  data: {
    id: number;
    uuid: string;
    real_identity: RealIdentity;
    wallet_address: string;
    alias_iban: FullAliasIBAN;
    onchainidTCs: string;
    holderId: string;
    created_at: string;
    updated_at: string;
    deleted_date: string | null;
  };
};
export type CreateWalletReq = {
  walletAddress: string;
  wallet_connect: boolean;
  olky_wallet: boolean;
};

export type SendKypayToUserWalletProps = {
  amount: number;
};
export type SendToKypayProps = {
  amount: number;
};
export type SendKypayToUserWalletReponse = {
  success: boolean;
  data: {
    transactionHash: string;
    gasUsed: number;
  };
};
export type SendToKypayWallettReponse = {
  success: boolean;
  data: { type: string; data: string; transferDetails: retTransfer };
};
export type SendToKypayReponse = {
  success: boolean;
  data: { type: string; data: SendKyPayTransaction };
};
export type retTransfer = {
  uuid_wallet: string;
  destination_address: string;
  contract_address: string;
  amount: string;
  status: string;
};
export type SendKyPayTransaction = {
  to: string;
  from: string;
  data: string;
};
export type FeedKypayBalanceProps = {
  amount: number;
  description: string;
};
export type GenerateWalletDepositProps = {
  networkKey: string;
  assetKey: string;
  walletTravelRuleUuid: string;
};

export type GenerateCryptoAmountQuoteProps = {
  amount: string | null;
  assetKey: string | null;
};
export type generateCryptoAmountQuoteResponse = {
  data: number;
  sucess: boolean;
};
export type GenerateWalletDepositResponse = {
  data: string;
  success: boolean;
};
export type FeedKypayBalanceCardSessionProps = {
  amount: number;
  lang: string;
};

export type FeedKypayBalanceResponse = {
  url: string;
};
export type FeedKypayBalanceCardSessioneResponse = {
  session_id: string;
  reference: string;
};
export type WithdrawKypayBalanceProps = {
  amount: number;
  alias_iban_uuid: string;
};

export type WithdrawKypayBalanceResponse = null;
export type VirtualIban = {
  IBAN: string;
  BIC: string;
};
export type KypayAccount = {
  account_id: string;
  IDPartenaire: string;
  partner_name: string;
  allow_deposit_cb: boolean;
  partner_website?: string;
  is_principal_account: boolean;
  virtual_iban?: VirtualIban;
  balance?: number;
  balance_onchain?: number;
  symbol?: string;
};

export type SearchIdentityParams = {
  keyword: string;
};

export type MinifiedOlkyAccount = {
  iban: string;
  supplierId: string;
  codeShop: string;
};
export type SearchOlkyPassIdentity = {
  uuid: string;
  firstName: string;
  lastName: string;
  olkyAccount?: MinifiedOlkyAccount | null;
  olkyPassLevel?: KYCSessionStatus;
};
export type TransferKypayAmountProps = {
  to?: string;
  amount: number;
  type?: string;
};

export type TransferKypayAmountResponse = {
  code: string;
  NaturalPerson: string;
};
export type TransactionResponse = {
  transactions: KypayTransaction[];
  count: number;
};
export type WalletDepositProps = {
  allowedToKypay: boolean;
  countries: FullCountry[];
  isPendingCreation: boolean;
  walletRule?: walletsTravelRules | null;
  onValidate: (data: SchemaRequestCreateTravelRule) => void;
  isPerson: boolean;
  disable: boolean;
};
export enum TravelRuleWalletType {
  SELF = "SELF",
  THIRD_PARTY = "THIRD_PARTY"
}

export interface walletsTravelRules {
  uuid: string;
  isSamePerson: boolean;
  walletName: string;
  isCompany: boolean;
  companyName: string | null;
  companyAddress: string | null;
  companyRcs: string | null;
  companyCountryAlpha3: string | null;
  userLastName: string | null;
  userFirstName: string | null;
  userCountryAlpha3: string | null;
  walletType: TravelRuleWalletType;
  walletTypeName: string | null;
}
export interface walletTravelRuleAction {
  uuid: string;
  walletName: string;
  walletTypeName: string;
}

export type travelRulesWalletItems = walletsTravelRules | walletTravelRuleAction;

export enum TravelRulePersonType {
  NATURAL = "NATURAL",
  LEGAL = "LEGAL"
}
export interface SchemaRequestCreateTravelRule {
  realIdentityUuid: string;
  isSamePerson: boolean;
  name: string | null;
  type: TravelRuleWalletType | null;
  typeName: string | null;
  partnerUuid?: string | undefined | null;
  companyInformation?:
    | {
        name: string | null;
        address: string | null;
        rcs: string | null | undefined;
        countryCodeAlpha3: string | null;
      }
    | null
    | undefined;
  naturalInformation?:
    | {
        firstName: string | null;
        lastName: string | null;
        countryCodeAlpha3: string | null;
      }
    | null
    | undefined;
}
